import { isEmpty } from 'lodash';
import { useCallback, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  useGetAssembliesWithoutPaginationAndLayout,
  useParamQuery,
  useUpdateAssemblyWithAssemblyTags,
} from 'src/hooks';
import { useDeleteAssemblyTag } from 'src/hooks/api/assemblyTags/useDeleteAssemblyTag';
import { useGetAssemblyTagsWithAssembliesCount } from 'src/hooks/api/assemblyTags/useGetAssemblyTagsWithAssembliesCount';
import { formatQuery, lang } from 'src/utils';

import {
  ASSEMBLY_TAG_LIST_COLUMN,
  Column,
} from '../ManageAssemblyTagPage.interface';
import { getTotalLengthPerPage } from '../modules/ManageAssemblyTag.helper';

export const useManageAssemblyTagPage = () => {
  const location = useLocation();

  const { data: assemblies = [] } =
    useGetAssembliesWithoutPaginationAndLayout();
  const updateAssemblies = useUpdateAssemblyWithAssemblyTags();
  const deleteAssemblyTag = useDeleteAssemblyTag();

  const {
    data: assemblyTagWithAssembliesCount,
    isLoading,
    hasNextPage,
    fetchNextPage,
  } = useGetAssemblyTagsWithAssembliesCount(formatQuery(location.search));

  const totalCount =
    assemblyTagWithAssembliesCount?.pages?.[0]?.totalCount || 0;
  const numberofResults = getTotalLengthPerPage(
    assemblyTagWithAssembliesCount?.pages ?? [],
  );
  const [activeColumn, setActiveColumn] = useState<string>(
    ASSEMBLY_TAG_LIST_COLUMN.NAME,
  );

  const [sorting, setSorting] = useState({
    columnKey: ASSEMBLY_TAG_LIST_COLUMN.NAME,
    order: 'asc',
  });

  const [isActive, setIsActive] = useState(false);

  const { activeFilters, onChangeParams } = useParamQuery({
    sort: [ASSEMBLY_TAG_LIST_COLUMN.NAME],
  });

  const getNextAssemblyTagWithAssembliesCountPage = useCallback(() => {
    if (hasNextPage) {
      fetchNextPage();
    }
  }, [fetchNextPage, hasNextPage]);

  const handleSort = (columnKey: ASSEMBLY_TAG_LIST_COLUMN, order: string) => {
    setSorting(() => ({
      columnKey,
      order,
    }));
    setActiveColumn(columnKey);
    setIsActive(true);
    onChangeParams('sort', [
      `${order === 'asc' ? columnKey : `-${columnKey}`}`,
    ]);
  };

  const columns: Column[] = [
    {
      label: lang.assemblyTagText,
      key: ASSEMBLY_TAG_LIST_COLUMN.NAME,
      sortIcon: true,
    },
    {
      label: lang.numberOfAssemblies,
      key: ASSEMBLY_TAG_LIST_COLUMN.ASSEMBLIES_COUNT,
      sortIcon: true,
    },
    { label: '', key: ASSEMBLY_TAG_LIST_COLUMN.ACTIONS, sortIcon: false },
  ];

  return {
    assemblyTagWithAssembliesCount,
    isLoading,
    hasActiveFilters: !isEmpty(activeFilters),
    getNextAssemblyTagWithAssembliesCountPage,
    onChangeParams,
    columns,
    handleSort,
    isActive,
    activeColumn,
    sorting,
    hasNextPage,
    totalCount,
    numberofResults,
    assemblies,
    deleteAssemblyTag,
    updateAssemblies,
    activeFilters,
  };
};
